import { useEffect, useRef, useState } from "react"

import BottomGlow from "@/assets/img/soulmate/loading-bottom-glow.png"
import Logo from "@/assets/img/soulmate/logo.png"
import TopGlow from "@/assets/img/soulmate/loading-top-glow.png"
import type { StepComponentProps } from "./step-contract"

/** Message slides (Figma Loading section). The first slide also shows the logo. */
const SLIDES = [
  "Welcome to Live Palmistry",
  "Based on your answers and goals",
  "We've created your Soulmate Sketch",
  "To help you meet the right partner",
  "Find out what your soulmate looks like today",
]

const SLIDE_MS = 1600 // total time per slide (fade in + hold + fade out)
const FADE_MS = 450 // fade-out / fade-in duration
const HOLD_MS = SLIDE_MS - FADE_MS // fully-visible time before fading out

/**
 * Full-bleed Soulmate loading screen (Figma Loading section): a sequence of five
 * centered Kalnia message slides over a fixed top + bottom glow, the logo on the
 * first slide. No progress bar by design — it auto-advances on a timer, then
 * routes onward (`onAdvance`). Wired as a per-step `component`.
 */
export function SoulmateLoadingStep({ onAdvance }: StepComponentProps) {
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(false)
  const onAdvanceRef = useRef(onAdvance)
  useEffect(() => {
    onAdvanceRef.current = onAdvance
  })

  // Per slide: fade in → hold → fade out → advance to the next (or finish). All
  // setState runs inside timers, never synchronously in the effect body, so the
  // opacity transition fires and ESLint's set-state-in-effect stays happy.
  useEffect(() => {
    const fadeIn = setTimeout(() => setVisible(true), 30)
    const fadeOut = setTimeout(() => setVisible(false), HOLD_MS)
    const next = setTimeout(() => {
      if (index >= SLIDES.length - 1) onAdvanceRef.current()
      else setIndex((i) => i + 1)
    }, HOLD_MS + FADE_MS)
    return () => {
      clearTimeout(fadeIn)
      clearTimeout(fadeOut)
      clearTimeout(next)
    }
  }, [index])

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-dark-base text-purple-light">
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[264px] w-full object-cover"
      />
      <img
        src={BottomGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[264px] w-full object-cover"
      />

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-10 text-center">
        <div
          className="flex flex-col items-center gap-4 will-change-[opacity]"
          style={{ opacity: visible ? 1 : 0, transition: `opacity ${FADE_MS}ms ease` }}
        >
          {index === 0 && <img src={Logo} alt="" className="size-22 rounded-[25px]" />}
          <h1 className="font-display text-[32px] font-medium leading-snug text-purple-light">
            {SLIDES[index]}
          </h1>
        </div>
      </div>
    </div>
  )
}
